import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import DateRangeIcon from '@mui/icons-material/DateRange';
import Box from '@mui/material/Box';
import IconButton, { type IconButtonProps } from '@mui/material/IconButton';
import Popover, { type PopoverProps } from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import type { ButtonProps } from '@mui/material/Button';
import { StaticDateRangePicker } from '@mui/x-date-pickers-pro';
import { QSelectButton } from 'components/QDropdowns';
import { DateTime, Interval } from 'luxon';
import React, { useMemo, useRef, useReducer } from 'react';
import type { DateRangeType } from './types';
import { getDatesForId, getDisplayedRangeForId, getIdForRange, getRangeForId } from './utils';
import CustomActionbar from './Actionbar';
import CustomLayout from './Layout';
import CustomShortcuts, { type CustomShortcutsProps } from './Shortcuts';
import ShortcutsSelect from './ShortcutsSelect';
import CustomToolbar from './Toolbar';
import useStyles from './styles';
import type { DynamicRangeType } from './DynamicView';
import { dynamicRangeTypeToReportRangeType, reportRangeTypeToDynamicRangeType } from './DynamicView';
import {
  formatDynamicRangeLabel,
  formatFixedRangeLabel,
  type DynamicRangeIntervalTimestep,
} from 'app-components/utils/date';
import { isGeneratedReportRangeTypeDynamic } from 'app-components/utils/date/predefinedRange';

const FALLBACK_MIN_DATE = DateTime.fromObject({ year: 1900, month: 1, day: 1 });

// Helper to create intervals safely
const createInterval = (startDate: DateTime | undefined, endDate: DateTime | undefined) => {
  if (startDate && endDate && startDate.isValid && endDate.isValid) {
    return Interval.fromDateTimes(startDate, endDate);
  }
  return null;
};

type DateRangeChangeType = {
  start: DateTime | undefined;
  end: DateTime | undefined;
  selectedRangeId?: string | 'Custom';
  defaultRangeId: string;
  id?: string;
  interval: Interval | null;
  dateRangeInterval?: DynamicRangeIntervalTimestep;
  dateRangeIntervalCount?: number;
};

type InitStateProps = {
  startingDate?: DateTime;
  endingDate?: DateTime;
  ranges?: DateRangeType[];
  selectedRangeId?: string;
  defaultRangeId?: string;
  emptySelectionId?: string;
  maxDaysBefore?: number;
  maxDaysAfter?: number;
  minDate?: DateTime;
  maxDate?: DateTime;
  dateRangeInterval?: DynamicRangeIntervalTimestep;
  dateRangeIntervalCount?: number;
};

const initState = ({
  startingDate,
  endingDate,
  ranges,
  emptySelectionId,
  selectedRangeId,
  defaultRangeId,
  maxDaysBefore,
  maxDaysAfter,
  minDate: minDateProp,
  maxDate: maxDateProp,
  dateRangeInterval,
  dateRangeIntervalCount,
}: InitStateProps) => {
  const derivedRange = selectedRangeId ?? emptySelectionId;
  const selectedRange = getRangeForId(derivedRange, ranges);
  let rangeStart, rangeEnd;

  if (selectedRange && ranges) {
    const rangesMap = new Map(ranges.map((range) => [range.id, range]));
    const dates = getDatesForId(rangesMap, derivedRange);
    [rangeStart, rangeEnd] = dates || [undefined, undefined];
  } else {
    [rangeStart, rangeEnd] = [undefined, undefined];
  }
  const startDate = startingDate || rangeStart || DateTime.local();
  const endDate = endingDate || rangeEnd || DateTime.local().plus({ day: 30 });
  const predefinedRanges = ranges || [];
  const shortCutsMap = ranges ? new Map(ranges.map((i) => [i.id, i])) : new Map();
  // emptySelectionId only applies when the parent provided no dates at all.
  // If the parent passed custom dates that don't match any shortcut, fall to
  // 'CUSTOM' rather than the empty fallback
  const hasUserDates = Boolean(startingDate || endingDate);
  const selectedShortcutId =
    selectedRangeId ??
    getIdForRange(predefinedRanges, startDate, endDate) ??
    (hasUserDates ? 'CUSTOM' : (emptySelectionId ?? 'CUSTOM'));
  const displayedRange = getDisplayedRangeForId(ranges, selectedShortcutId);
  const selectedDefaultId = defaultRangeId || null;
  const rangePosition = 'start'; // 'start' or 'end' depending on which date is to be set
  const minDate =
    minDateProp ??
    (typeof maxDaysBefore === 'number' && maxDaysBefore >= 0
      ? DateTime.local().startOf('day').minus({ day: maxDaysBefore })
      : FALLBACK_MIN_DATE);
  const maxDate =
    maxDateProp ??
    (typeof maxDaysAfter === 'number' && maxDaysAfter >= 0
      ? DateTime.local().endOf('day').plus({ day: maxDaysAfter })
      : null);

  const anchorEl: Extract<PopoverProps, 'anchorEl'> | null = null;

  // Captures the exact start/end the parent passed in, before any fallback substitution.
  // Used by handleClose to distinguish "parent had a real selection to revert to" from
  // "we fabricated today / today+30 just so the calendar had something to render."
  const originalState: { start?: DateTime; end?: DateTime } = {
    start: startingDate,
    end: endingDate,
  };

  return {
    startDate,
    endDate,
    predefinedRanges,
    shortCutsMap,
    selectedShortcutId,
    displayedRange,
    selectedDefaultId,
    rangePosition,
    minDate,
    maxDate,
    errors: { isValidRange: true },
    anchorEl,
    originalState,
    dynamicInterval: dateRangeInterval as DynamicRangeIntervalTimestep | undefined,
    dynamicCount: dateRangeIntervalCount as number | undefined,
  };
};

type DateRangeState = ReturnType<typeof initState>;

// Helper to create onSelect payload
const createSelectPayload = (
  state: DateRangeState,
  startDate: DateTime | undefined,
  endDate: DateTime | undefined,
  selectedRangeId: string | undefined,
  interval = null,
): DateRangeChangeType => ({
  start: startDate,
  end: endDate,
  interval: interval || createInterval(startDate, endDate),
  selectedRangeId,
  defaultRangeId: state.selectedDefaultId ?? '',
  dateRangeInterval: state.dynamicInterval,
  dateRangeIntervalCount: state.dynamicCount,
});

// Helper to create shortcut selection payload
const createShortcutPayload = (
  startDate: DateTime | undefined,
  endDate: DateTime | undefined,
  selectedShortcutId: string | undefined,
  displayedRange: string,
) => ({
  startDate,
  endDate,
  selectedShortcutId,
  displayedRange,
});

// Validation helpers
const validateDate = (
  date: DateTime | undefined,
  minDate: DateTime | null,
  maxDate: DateTime | null,
  dateType: string,
) => {
  if (!date || !date.isValid) {
    return `Input error: ${dateType} date is not valid`;
  }
  if (minDate && date < minDate) {
    return `Out of range: select a date on or after ${minDate.toLocaleString()}`;
  }
  if (maxDate && date > maxDate) {
    return `Out of range: select a date on or before ${maxDate.toLocaleString()}`;
  }
  return null;
};

function verifyRange(
  minDate: DateTime | null,
  maxDate: DateTime | null,
  selectedStart: DateTime | undefined,
  selectedEnd: DateTime | undefined,
) {
  // Validate individual dates
  const startHelperText = validateDate(selectedStart, minDate, maxDate, 'start');
  let endHelperText = validateDate(selectedEnd, minDate, maxDate, 'end');

  // Validate date relationship
  if (selectedEnd && selectedStart && selectedEnd < selectedStart) {
    endHelperText = 'Invalid: End date must be on or after Start date';
  }

  const isValidRange = !(startHelperText || endHelperText || selectedStart === null || selectedEnd === null);

  return {
    startHelperText,
    endHelperText,
    isValidRange,
  };
}

function getSelectValuesForDates(
  predefinedRanges: DateRangeType[],
  startDate: DateTime | undefined,
  endDate: DateTime | undefined,
  currentShortcutId?: string | null,
) {
  // Preserve dynamic selection even when dates are missing/invalid — clearing the shortcut would
  // collapse the dynamic view back to the calendar, so we preserve it and let verifyRange handle
  // the isValidRange=false signal for Apply.
  if (currentShortcutId && isGeneratedReportRangeTypeDynamic(currentShortcutId as any)) {
    return {
      startDate,
      endDate,
      selectedShortcutId: currentShortcutId,
      displayedRange: getDisplayedRangeForId(undefined, currentShortcutId),
    };
  }

  // Return early if dates are missing
  if (!startDate || !endDate) {
    return { startDate, endDate, selectedShortcutId: null, displayedRange: null };
  }

  // Find matching shortcut or default to custom
  const selectedShortcutId = getIdForRange(predefinedRanges, startDate, endDate) || 'CUSTOM';
  const displayedRange =
    selectedShortcutId === 'CUSTOM' ? 'Custom' : getDisplayedRangeForId(predefinedRanges, selectedShortcutId);

  return { startDate, endDate, selectedShortcutId, displayedRange };
}

function reducer(state: DateRangeState, action: any) {
  switch (action.type) {
    case 'calendarDisplayed':
      return { ...state, anchorEl: action.payload };
    case 'cancel':
      return { ...action.payload };
    case 'propsResynced':
      return {
        ...action.payload,
        anchorEl: state.anchorEl,
        rangePosition: state.rangePosition,
      };
    case 'shortcutSelected': {
      const errors = verifyRange(state.minDate, state.maxDate, action.payload.startDate, action.payload.endDate);
      return { ...state, ...action.payload, errors: { ...errors } };
    }
    case 'defaultSelected':
      return { ...state, ...action.payload };
    case 'dateRangeChanged': {
      const errors = verifyRange(state.minDate, state.maxDate, action.payload.startDate, action.payload.endDate);
      const updates = getSelectValuesForDates(
        state.predefinedRanges,
        action.payload.startDate,
        action.payload.endDate,
        state.selectedShortcutId,
      );
      const newRangePosition =
        action.payload.rangePosition !== undefined ? action.payload.rangePosition : state.rangePosition;
      return { ...state, ...updates, errors: { ...errors }, rangePosition: newRangePosition };
    }
    case 'inputFocus': {
      return { ...state, rangePosition: action.payload };
    }
    case 'dynamicSelectionChanged': {
      const reportRangeType = dynamicRangeTypeToReportRangeType(action.payload);
      return {
        ...state,
        selectedShortcutId: reportRangeType,
        displayedRange: getDisplayedRangeForId(state.predefinedRanges, reportRangeType),
      };
    }
    case 'dynamicMetaChanged': {
      return { ...state, dynamicInterval: action.payload.interval, dynamicCount: action.payload.count };
    }
    default:
      return state;
  }
}

export type QDateRangePickerProps = {
  id?: string;
  anchorEl?: PopoverProps['anchorEl'];
  startingDate?: DateTime;
  endingDate?: DateTime;
  popoverProps?: Partial<PopoverProps>;
  ranges?: DateRangeType[];
  emptySelectionId?: string;
  selectedRangeId?: string;
  defaultRangeId?: string;
  onSelect: (payload: DateRangeChangeType) => void;
  onClose?: () => void;
  disablePastDates?: boolean;
  disableFutureDates?: boolean;
  maxDaysBefore?: number;
  maxDaysAfter?: number;
  minDate?: DateTime;
  maxDate?: DateTime;
  allowCustomRange?: boolean; // if false, show ranges as a dropdown.  if true, show calendars to allow selection
  allowReset?: boolean; // if true, the picker's actionbar shows a Reset button that clears the selection
  compact?: boolean; // use a compact input rendering
  /** Trigger button size. Defaults to `"small"` when `compact`, otherwise `"medium"`. */
  size?: ButtonProps['size'];
  renderInput?: (onClickHandler: (e?: any) => void, meta?: { open: boolean }) => React.ReactNode;
  showDynamic?: boolean; // if true, adds a Dynamic option to the shortcuts sidebar with its own view
  dateRangeInterval?: DynamicRangeIntervalTimestep | undefined; // used by dynamic view
  dateRangeIntervalCount?: number | undefined; // used by dynamic view
};
const ArrowNavButton = React.forwardRef<HTMLButtonElement, IconButtonProps & { ownerState?: { hidden?: boolean } }>(
  ({ ownerState, sx, ...props }, ref) => (
    <IconButton
      {...props}
      ref={ref}
      size='small'
      sx={{
        border: '1px solid',
        borderColor: 'border.light',
        borderRadius: '6px',
        width: 30,
        height: 30,
        ...(ownerState?.hidden && { visibility: 'hidden' }),
        ...sx,
      }}
    />
  ),
);
ArrowNavButton.displayName = 'ArrowNavButton';

const QDateRangePicker = (props: QDateRangePickerProps) => {
  const {
    id,
    onSelect,
    allowCustomRange,
    allowReset,
    compact,
    size,
    disablePastDates,
    disableFutureDates,
    popoverProps,
    renderInput,
    showDynamic,
    dateRangeInterval,
    dateRangeIntervalCount,
    ...rest
  } = props;
  const initialState = useMemo(
    () => initState({ ...rest, dateRangeInterval, dateRangeIntervalCount }),
    [rest, dateRangeInterval, dateRangeIntervalCount],
  );
  const [state, dispatch] = useReducer(reducer, initialState);

  // Helper to handle range selection logic
  const selectRange = (selectedRangeId: string | undefined) => {
    if (selectedRangeId === 'CUSTOM') {
      return createShortcutPayload(rest.startingDate, rest.endingDate, 'CUSTOM', 'Custom');
    } else if (selectedRangeId && isGeneratedReportRangeTypeDynamic(selectedRangeId as any)) {
      // Dynamic ranges are not in the shortcutsMap; dates are computed by DynamicView
      return createShortcutPayload(
        rest.startingDate,
        undefined,
        selectedRangeId,
        getDisplayedRangeForId(state.predefinedRanges, selectedRangeId),
      );
    } else {
      const [startDate, endDate] = getDatesForId(state.shortCutsMap, selectedRangeId) || [undefined, undefined];
      const displayedRange = getDisplayedRangeForId(state.predefinedRanges, selectedRangeId);
      return createShortcutPayload(startDate ?? undefined, endDate ?? undefined, selectedRangeId, displayedRange);
    }
  };

  // Sync state with prop changes
  React.useEffect(() => {
    if (rest.selectedRangeId != null && rest.selectedRangeId !== '') {
      const payload = selectRange(rest.selectedRangeId);
      dispatch({ type: 'shortcutSelected', payload });
      // interval/count are only meaningful for CUSTOM_LAST and CUSTOM_RECENT; CUSTOM_SINCE is
      // driven by startOn (synced above via startingDate). shortcutSelected does not carry the
      // dynamic meta, so keep it in sync with props for last/recent (e.g. count 3 -> 5).
      if (rest.selectedRangeId === 'CUSTOM_LAST' || rest.selectedRangeId === 'CUSTOM_RECENT') {
        dispatch({
          type: 'dynamicMetaChanged',
          payload: { interval: dateRangeInterval, count: dateRangeIntervalCount },
        });
      }
    } else {
      // Ensure that we respect the derived computation on initialization
      dispatch({ type: 'propsResynced', payload: initialState });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rest.selectedRangeId,
    rest.startingDate,
    rest.endingDate,
    rest.minDate,
    rest.maxDate,
    rest.maxDaysBefore,
    rest.maxDaysAfter,
    dateRangeInterval,
    dateRangeIntervalCount,
  ]);

  // Component setup
  const buttonRef = useRef(null);
  const { classes } = useStyles();
  const buttonSize = size ?? (compact ? 'small' : 'medium');

  // Event handlers
  const handleDateSelection = ([startDate, endDate]: [DateTime | undefined, DateTime | undefined]) => {
    const nextPosition = state.rangePosition === 'start' ? 'end' : 'start';
    dispatch({ type: 'dateRangeChanged', payload: { startDate, endDate, rangePosition: nextPosition } });
  };

  const handleShortcutSelect = (selectedShortcutId: string | undefined) => {
    if (!selectedShortcutId) return;

    if (selectedShortcutId === 'CUSTOM') {
      // Handle custom selection - keep current dates but update selection
      const payload = createShortcutPayload(state.startDate, state.endDate, 'CUSTOM', 'Custom');
      dispatch({ type: 'shortcutSelected', payload });
    } else if (selectedShortcutId === 'DYNAMIC') {
      const dynamicType = reportRangeTypeToDynamicRangeType(state.selectedShortcutId as any) ?? 'since';
      dispatch({ type: 'dynamicSelectionChanged', payload: dynamicType });
    } else {
      // Handle predefined range selection
      const payload = selectRange(selectedShortcutId);
      dispatch({ type: 'shortcutSelected', payload });

      if (!allowCustomRange) {
        // Immediately notify parent for non-custom range selections
        const selectPayload = createSelectPayload(state, payload.startDate, payload.endDate, selectedShortcutId);
        onSelect(selectPayload);
      }
    }
  };

  const handleAccept = () => {
    dispatch({ type: 'calendarDisplayed', payload: null });
    const selectPayload = createSelectPayload(state, state.startDate, state.endDate, state.selectedShortcutId);
    onSelect(selectPayload);
  };

  const handleReset = () => {
    dispatch({ type: 'calendarDisplayed', payload: null });
    const selectPayload = createSelectPayload(state, undefined, undefined, state.selectedDefaultId ?? '', null);
    onSelect(selectPayload);
  };

  const handleClose = () => {
    dispatch({ type: 'cancel', payload: initialState });

    // Dynamic ranges are only reachable via the calendar (showDynamic) mode where onSelect
    // fires only on explicit accept — so the parent's Redux state never changes mid-session
    // and there is nothing to revert. DynamicView also always mutates state on mount
    // (e.g. endDate=today vs the initialState fallback), making hasChanges structurally
    // true even without user interaction. Skipping onSelect here is safe.
    if (isGeneratedReportRangeTypeDynamic(state.selectedShortcutId as any)) return;

    const hasChanges =
      state.startDate !== initialState.startDate ||
      state.endDate !== initialState.endDate ||
      state.selectedShortcutId !== initialState.selectedShortcutId ||
      state.dynamicInterval !== initialState.dynamicInterval ||
      state.dynamicCount !== initialState.dynamicCount;

    if (hasChanges) {
      // Revert to the parent's original props, not the possibly modified initialState.startDate/endDate
      const selectPayload = createSelectPayload(
        initialState,
        initialState.originalState.start,
        initialState.originalState.end,
        initialState.selectedShortcutId,
      );
      onSelect(selectPayload);
    }
  };

  const handleDefaultSelect = (selectedDefaultId: string | undefined) =>
    selectedDefaultId && dispatch({ type: 'defaultSelected', payload: { selectedDefaultId } });

  const handleInputFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    if ('select' in event.target && typeof event.target.select === 'function') {
      event.target.select();
    }
    // Set range position based on which input was focused
    const inputId = event.target.id;
    const rangePosition = inputId === 'start' ? 'start' : 'end';
    dispatch({ type: 'inputFocus', payload: rangePosition });
  };

  const handleInputChange = (input: 'start' | 'end', value: DateTime | undefined) => {
    const startDate = input === 'start' ? value : state.startDate;
    const endDate = input === 'end' ? value : state.endDate;
    const rangePosition = input === 'start' ? 'end' : 'start';

    dispatch({ type: 'dateRangeChanged', payload: { startDate, endDate, rangePosition } });
  };

  const handleInputClear = (input: string) => {
    const startDate = input === 'start' ? null : state.startDate;
    const endDate = input === 'end' ? null : state.endDate;
    const rangePosition = !startDate && !endDate ? 'start' : input;
    dispatch({ type: 'dateRangeChanged', payload: { startDate, endDate, rangePosition } });
  };

  const handleChangeDynamicSelection = (selection: DynamicRangeType) => {
    dispatch({ type: 'dynamicSelectionChanged', payload: selection });
  };

  const handleDynamicDateChange = (
    start: DateTime,
    end: DateTime,
    interval?: DynamicRangeIntervalTimestep,
    count?: number,
  ) => {
    handleDateSelection([start, end]);
    dispatch({ type: 'dynamicMetaChanged', payload: { interval, count } });
  };

  // Display helpers
  const dateRangeString = () => {
    const defaultValue = 'Select dates';
    const errorValue = 'Invalid dates';

    const formatFixedDates = (): string => {
      if (!state.startDate || !state.endDate) {
        return defaultValue;
      }

      // Normalize dates to DateTime objects if needed
      const startDate = state.startDate?.toLocaleString ? state.startDate : DateTime.fromISO(state.startDate);
      const endDate = state.endDate?.toLocaleString ? state.endDate : DateTime.fromISO(state.endDate);

      if (!startDate.isValid || !endDate.isValid) {
        return errorValue;
      }

      return formatFixedRangeLabel(startDate, endDate);
    };

    switch (state.displayedRange) {
      case 'Custom':
        return formatFixedDates();

      case 'Since': {
        if (!state.startDate) {
          return defaultValue;
        }

        const startDate = state.startDate?.toLocaleString ? state.startDate : DateTime.fromISO(state.startDate);

        if (!startDate.isValid) {
          return errorValue;
        }

        return formatDynamicRangeLabel({ type: 'CUSTOM_SINCE', startDate });
      }

      case 'Last':
      case 'Recent': {
        const interval = state.dynamicInterval ?? dateRangeInterval;
        const count = state.dynamicCount ?? dateRangeIntervalCount;
        if (!interval || !count) return defaultValue;

        const type = state.displayedRange === 'Last' ? 'CUSTOM_LAST' : 'CUSTOM_RECENT';
        return formatDynamicRangeLabel({ type, interval, count });
      }

      default:
        return formatFixedDates();
    }
  };

  const shouldDisableDate = (date: DateTime) =>
    (state.minDate && date < state.minDate) || (state.maxDate && date > state.maxDate);

  const showCalendar = (e?: React.MouseEvent<HTMLElement>) => {
    const anchor = e?.currentTarget ?? buttonRef.current;
    dispatch({ type: 'calendarDisplayed', payload: anchor });
  };

  const renderDefaultInput = (onClickHandler: (e?: any) => void) => {
    const shouldUseDateRangeString = compact && ['Custom', 'Since', 'Last', 'Recent'].includes(state.displayedRange);
    return (
      <QSelectButton
        id='date-range-menu'
        data-testid='date-range-menu'
        onClick={onClickHandler}
        ref={buttonRef}
        open={Boolean(state.anchorEl)}
        size={buttonSize}
        startIcon={<DateRangeIcon />}
      >
        <span>{shouldUseDateRangeString ? dateRangeString() : state.displayedRange}</span>
      </QSelectButton>
    );
  };

  // Main component render
  const renderInputMeta = { open: Boolean(state.anchorEl) };
  return (
    <Box className={classes.dateRangeContainer}>
      {allowCustomRange ? (
        <>
          {renderInput ? renderInput(showCalendar, renderInputMeta) : renderDefaultInput(showCalendar)}

          <Popover
            id={id || 'date-range-picker-popover'}
            open={Boolean(state.anchorEl)}
            anchorEl={state.anchorEl}
            elevation={24}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            {...popoverProps}
            onClose={handleClose}
          >
            <div className={classes.root}>
              <StaticDateRangePicker
                shouldDisableDate={shouldDisableDate}
                value={[state.startDate, state.endDate]}
                onChange={handleDateSelection}
                disablePast={disablePastDates}
                disableFuture={disableFutureDates}
                minDate={state.minDate ?? undefined}
                maxDate={state.maxDate ?? undefined}
                rangePosition={state.rangePosition}
                slots={{
                  shortcuts: state.shortCutsMap.size || showDynamic ? CustomShortcuts : undefined,
                  layout: CustomLayout,
                  actionBar: CustomActionbar,
                  toolbar: CustomToolbar,
                  previousIconButton: ArrowNavButton,
                  nextIconButton: ArrowNavButton,
                  leftArrowIcon: ChevronLeftRounded,
                  rightArrowIcon: ChevronRightRounded,
                }}
                slotProps={{
                  shortcuts: {
                    shortcutItems: state.predefinedRanges,
                    selectedShortcutId: state.selectedShortcutId,
                    handleShortcutSelect,
                    showDynamic,
                    customLabel: showDynamic ? 'Fixed' : undefined,
                  } as CustomShortcutsProps,
                  layout: {
                    showDynamicView: isGeneratedReportRangeTypeDynamic(state.selectedShortcutId as any),
                    onDynamicClose: handleClose,
                    onDynamicDateChange: handleDynamicDateChange,
                    dynamicSelection: reportRangeTypeToDynamicRangeType(state.selectedShortcutId as any),
                    handleChangeDynamicSelection,
                    startDate: state.selectedShortcutId === 'CUSTOM_SINCE' ? state.startDate : undefined,
                    dateRangeInterval,
                    dateRangeIntervalCount,
                    minDate: state.minDate,
                  } as any,
                  actionBar: {
                    shortcutItems: state.predefinedRanges,
                    selectedDefaultId: state.selectedDefaultId,
                    handleAccept,
                    handleClose,
                    handleDefaultSelect,
                    isValidRange: state.errors.isValidRange,
                    showReset: Boolean(allowReset),
                    handleReset,
                  } as any,
                  toolbar: {
                    startDate: state.startDate,
                    endDate: state.endDate,
                    rangePosition: state.rangePosition,
                    handleInputChange,
                    handleInputFocus,
                    handleInputClear,
                    keyboardButtonClassName: classes.clearDateIcon,
                    errors: state.errors,
                  } as any,
                }}
                calendars={2}
              />
            </div>
          </Popover>
        </>
      ) : (
        <ShortcutsSelect
          shortcutItems={state.predefinedRanges}
          selectedId={state.selectedShortcutId}
          handleSelect={handleShortcutSelect}
        />
      )}

      {!compact && (
        <Typography variant='paragraphRegular' className={classes.dateRangeType}>
          {dateRangeString()}
        </Typography>
      )}
    </Box>
  );
};

export default QDateRangePicker;
